// src/components/admin/DeliveryChargesEditor.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { geocodeAddress } from '../../api/deliveryApi';
import '../../styles/delivery-charges-editor.css';

// Default tiers for new salons — Short/Long distance pricing brackets for
// product delivery.
const DEFAULT_TIERS = [
  { name: 'Short Distance', minKm: 1, maxKm: 10, charge: 15 },
  { name: 'Long Distance', minKm: 11, maxKm: null, charge: 25 } // null means unlimited
];

const DEFAULT_BASE_LOCATION = { address: '', lat: null, lng: null };

function DeliveryChargesEditor({ salonId, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [tierErrors, setTierErrors] = useState([]);

  const [baseAddressInput, setBaseAddressInput] = useState('');
  const [baseLocation, setBaseLocation] = useState(DEFAULT_BASE_LOCATION);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');

  const [tiers, setTiers] = useState(DEFAULT_TIERS);

  useEffect(() => {
    loadSettings();
  }, [salonId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = async () => {
    try {
      setLoading(true);

      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);

      if (salonDoc.exists()) {
        const data = salonDoc.data();
        const pd = data.productDelivery;

        if (pd) {
          const base = pd.baseLocation || DEFAULT_BASE_LOCATION;
          setBaseLocation(base);
          setBaseAddressInput(base.address || '');

          // Normalise tiers the same way LoyaltyProgramEditor does — Firestore
          // can return arrays as maps, and numeric fields can be missing.
          let rawTiers = pd.tiers;
          if (!rawTiers || !Array.isArray(rawTiers) || rawTiers.length === 0) {
            rawTiers = DEFAULT_TIERS;
          } else {
            // This feature supports exactly 2 tiers — truncate any legacy
            // 3-tier data saved before that was the case.
            if (rawTiers.length > 2) rawTiers = rawTiers.slice(0, 2);
            rawTiers = rawTiers.map((t, i) => ({
              name: t.name || `Tier ${i + 1}`,
              minKm: typeof t.minKm === 'number' ? t.minKm : (parseFloat(t.minKm) || 0),
              maxKm: t.maxKm === null || t.maxKm === undefined
                ? (i === rawTiers.length - 1 ? null : 0)
                : (typeof t.maxKm === 'number' ? t.maxKm : parseFloat(t.maxKm) || 0),
              charge: typeof t.charge === 'number' ? t.charge : (parseFloat(t.charge) || 0)
            }));
          }
          setTiers(rawTiers);
        }
      }
    } catch (error) {
      console.error('Error loading delivery charge settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocateBase = async () => {
    if (!baseAddressInput.trim()) {
      setLocateError('Enter an address first.');
      return;
    }
    setLocating(true);
    setLocateError('');
    try {
      const result = await geocodeAddress(salonId, baseAddressInput.trim());
      setBaseLocation({
        address: result.displayName || baseAddressInput.trim(),
        lat: result.lat,
        lng: result.lng
      });
    } catch (err) {
      console.error('Error geocoding base location:', err);
      setLocateError(err.message || 'Could not locate this address. Please refine it and try again.');
    } finally {
      setLocating(false);
    }
  };

  // Validate tier distance ranges and charges
  const validateTiers = (tiersList) => {
    const errors = [];

    tiersList.forEach((tier, i) => {
      if (!tier.name || tier.name.trim() === '') {
        errors.push({ index: i, message: `Tier ${i + 1} name cannot be empty` });
      }
      if (tier.minKm < 0) {
        errors.push({ index: i, message: `${tier.name || `Tier ${i + 1}`}: Minimum distance cannot be negative` });
      }
      if (tier.maxKm !== null && tier.maxKm < tier.minKm) {
        errors.push({ index: i, message: `${tier.name || `Tier ${i + 1}`}: Maximum distance must be greater than or equal to minimum distance` });
      }
      if (tier.charge < 0) {
        errors.push({ index: i, message: `${tier.name || `Tier ${i + 1}`}: Delivery charge cannot be negative` });
      }
    });

    return errors;
  };

  const handleTierField = (index, field, value) => {
    const newTiers = [...tiers];
    let val;
    if (field === 'name') {
      val = value;
    } else if (field === 'maxKm') {
      val = value === '' ? null : parseFloat(value);
    } else {
      val = value === '' ? 0 : parseFloat(value);
    }
    newTiers[index] = { ...newTiers[index], [field]: val };
    setTiers(newTiers);
    setTierErrors(validateTiers(newTiers));
  };

  const handleSave = async () => {
    const errors = validateTiers(tiers);
    if (errors.length > 0) {
      setTierErrors(errors);
      setSavedMessage('Error: Please fix the errors below before saving.');
      setTimeout(() => setSavedMessage(''), 5000);
      return;
    }

    if (baseLocation.lat == null || baseLocation.lng == null) {
      setSavedMessage('Error: Please locate your Product Base Location address before saving.');
      setTimeout(() => setSavedMessage(''), 5000);
      return;
    }

    try {
      setSaving(true);

      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);
      const productDelivery = { baseLocation, tiers };

      if (salonDoc.exists()) {
        await updateDoc(salonRef, { productDelivery, updatedAt: new Date() });
      } else {
        await setDoc(salonRef, {
          salonId,
          productDelivery,
          createdAt: new Date(),
          updatedAt: new Date()
        }, { merge: true });
      }

      setTierErrors([]);
      setSavedMessage('Delivery charge settings saved successfully!');
      setTimeout(() => setSavedMessage(''), 3000);

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving delivery charge settings:', error);
      setSavedMessage('Error saving settings. Please try again.');
      setTimeout(() => setSavedMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const getTierErrors = (index) => tierErrors.filter(e => e.index === index);

  if (loading) {
    return (
      <div className="delivery-charges-editor">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading delivery charge settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="delivery-charges-editor">
      <div className="delivery-header">
        <h2>Delivery Charges</h2>
        <p className="delivery-description">
          Set your product base location and the delivery cost for each distance range. Customers see these charges when they choose delivery at checkout.
        </p>
      </div>

      {savedMessage && (
        <div className={`save-message ${savedMessage.includes('Error') ? 'error' : 'success'}`}>
          {savedMessage}
        </div>
      )}

      <div className="delivery-sections">
        {/* Product Base Location */}
        <div className="delivery-section">
          <h3>Product Base Location</h3>
          <p className="section-description">
            The address delivery distances are calculated from — usually your store or warehouse.
          </p>
          <div className="base-location-row">
            <input
              type="text"
              className="base-location-input"
              placeholder="e.g. 21 Action Road, Singapore 418754"
              value={baseAddressInput}
              onChange={(e) => setBaseAddressInput(e.target.value)}
            />
            <button className="locate-btn" onClick={handleLocateBase} disabled={locating}>
              {locating ? 'Locating...' : 'Locate Address'}
            </button>
          </div>
          {locateError && (
            <div className="tier-error-message error">❌ {locateError}</div>
          )}
          {baseLocation.lat != null && baseLocation.lng != null && (
            <div className="base-location-confirmed">
              ✓ Location set: {baseLocation.address}
              <span className="base-location-coords">
                ({baseLocation.lat.toFixed(5)}, {baseLocation.lng.toFixed(5)})
              </span>
            </div>
          )}
        </div>

        {/* Delivery Tiers */}
        <div className="delivery-section">
          <h3>Delivery Cost by Distance</h3>
          <p className="section-description">
            Define the delivery charge for each distance range from your base location.
          </p>

          <div className="tiers-list">
            {tiers.map((tier, index) => {
              const errors = getTierErrors(index);
              const hasError = errors.length > 0;

              return (
                <div key={index} className={`tier-item ${hasError ? 'has-error' : ''}`}>
                  <div className="tier-header">
                    <span className="tier-number">Tier {index + 1}</span>
                    {index === tiers.length - 1 && (
                      <span className="tier-badge top-tier">Open-Ended</span>
                    )}
                  </div>

                  <div className="tier-fields">
                    <div className="tier-field">
                      <label>Tier Name</label>
                      <input
                        type="text"
                        value={tier.name}
                        onChange={(e) => handleTierField(index, 'name', e.target.value)}
                        placeholder="Enter tier name"
                        className={errors.some(e => e.message.includes('name')) ? 'input-error' : ''}
                      />
                    </div>

                    <div className="tier-field">
                      <label>Min Distance (km)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={tier.minKm}
                        onChange={(e) => handleTierField(index, 'minKm', e.target.value)}
                        className={errors.some(e => e.message.includes('Minimum')) ? 'input-error' : ''}
                      />
                    </div>

                    <div className="tier-field">
                      <label>Max Distance (km)</label>
                      {index === tiers.length - 1 ? (
                        <div className="unlimited-badge">No Limit</div>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={tier.maxKm === null ? '' : tier.maxKm}
                          onChange={(e) => handleTierField(index, 'maxKm', e.target.value)}
                          placeholder="No limit"
                          className={errors.some(e => e.message.includes('Maximum')) ? 'input-error' : ''}
                        />
                      )}
                    </div>

                    <div className="tier-field">
                      <label>Delivery Charge ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={tier.charge}
                        onChange={(e) => handleTierField(index, 'charge', e.target.value)}
                        className={errors.some(e => e.message.includes('charge')) ? 'input-error' : ''}
                      />
                    </div>
                  </div>

                  {hasError && (
                    <div className="tier-errors">
                      {errors.map((error, errIndex) => (
                        <div key={errIndex} className="tier-error-message error">
                          ❌ {error.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="tier-preview">
            <h4>Summary</h4>
            <div className="tier-preview-list">
              {tiers.map((tier, index) => (
                <div key={index} className="tier-preview-item">
                  <span className="tier-preview-name">{tier.name || `Tier ${index + 1}`}</span>
                  <span className="tier-preview-range">
                    {tier.minKm}–{tier.maxKm == null ? '∞' : tier.maxKm} km
                  </span>
                  <span className="tier-preview-charge">${Number(tier.charge || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="delivery-actions">
        <button
          className="save-button"
          onClick={handleSave}
          disabled={saving || tierErrors.length > 0}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

export default DeliveryChargesEditor;
